import { useRef, type ReactNode } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { useReducedMotion } from "@/utils/motion";

type Props = {
  children: ReactNode;
  /** Pixels travelled across the element's pass through the viewport. */
  distance?: number;
  /** `pass`: element scrolls through the viewport. `leave`: element starts on screen (heroes). */
  mode?: "pass" | "leave";
  className?: string;
};

/**
 * Moves its content at a different speed from the page as it scrolls,
 * using transforms only. Static when the visitor prefers reduced motion.
 */
export function Parallax({ children, distance = 80, mode = "pass", className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: mode === "leave" ? ["start start", "end start"] : ["start end", "end start"],
  });
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    mode === "leave" ? [0, distance] : [distance / 2, -distance / 2],
  );

  return (
    <motion.div ref={ref} style={reduced ? undefined : { y }} className={className}>
      {children}
    </motion.div>
  );
}
